/**
 * Compact inline price status indicator for flight/room cards.
 * Reads directly from the Firebolt model so signal reactivity works automatically.
 *
 * - Checkmark: price recently checked and accurate
 * - Up arrow: price increased since original search
 * - Down arrow: price decreased since original search
 * - Refresh icon + "Refresh" text: price is stale and needs rechecking
 */

import type { PriceDelta } from '@elara/client-core/lib/price-utils';
import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { formatDate, formatTimeSince, toDate } from '@elara/client-core/lib/date-utils';
import { getPriceDelta } from '@elara/client-core/lib/price-utils';
import { cn, formatPrice } from '@elara/client-core/lib/utils';

import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';

import { IconSizes, MaterialIcon } from './MaterialIcon.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

/** Shared shape that both FlightOptionModel and RoomOptionModel satisfy. */
interface PricedModel {
  id: string;
  fetchedAt?: Date | null;
  isStale: boolean;
  isAvailable: boolean;
  cost?: { amount: number; currency: string };
  priceDelta: PriceDelta | null;
  priceHistory?: Array<{ fetchedAt?: Date | null; pricing?: unknown; cost?: unknown }>;
}

type PriceStatusIndicatorProps = {
  /** Divide the price delta by this number to show per-unit changes (e.g. per passenger). Defaults to 1. */
  priceDivisor?: number;
  /** Label for the unit when priceDivisor > 1 (e.g. "per passenger", "per night"). */
  priceUnit?: string;
  /** Extra classes for the indicator. */
  className?: string;
} & (
  | { type: 'flight'; model: FlightOptionModel & PricedModel }
  | { type: 'room'; model: RoomOptionModel & PricedModel; productCode?: string; occupancy?: Occupancy }
);

/**
 * Consistent wrapper for all indicator states. Aligned by its text's baseline, with only the icon
 * centred, so a parent row aligned `items-baseline` sets the amount on the price's baseline.
 */
const indicatorBase = 'inline-flex items-baseline gap-0.5 rounded text-2xs font-medium [&>:first-child]:self-center';

export function PriceStatusIndicator(props: PriceStatusIndicatorProps) {
  const { type, model, priceDivisor = 1, priceUnit, className } = props;

  const label = type === 'flight' ? props.model.displayTitle('all') : props.model.displayName;
  const canRefresh = useCanRefreshPricing(type, model);

  const { isRefreshing, lastResult, refresh } = useRefreshPricing({
    type,
    referenceId: model.id,
    enabled: canRefresh,
    showToasts: true,
    label,
  });

  // Read reactive properties directly from the model
  const fetchedAt = toDate(model.fetchedAt);
  const timeAgo = fetchedAt ? formatTimeSince(fetchedAt) : 'a while ago';
  const isStale = model.isStale;

  // For rooms with a selected rate, compute delta from that rate's history.
  // Identity is `(productCode, occupancy)`.
  const productCode = type === 'room' ? (props as { productCode?: string }).productCode : undefined;
  const occupancy = type === 'room' ? (props as { occupancy?: Occupancy }).occupancy : undefined;
  const selectedRate =
    productCode && type === 'room' ? props.model.getRateByIdentity({ productCode, occupancy }) : undefined;

  let priceDelta: PriceDelta | null;
  let currency: string | undefined;
  let priceHistory: Array<{ fetchedAt?: Date | null }> | undefined;

  if (selectedRate) {
    const rateHistory = selectedRate.priceHistory;
    const originalPricing = rateHistory?.at(-1)?.pricing;
    priceDelta = originalPricing
      ? getPriceDelta(
          { amount: selectedRate.localPricing.totalPrice, currency: selectedRate.localPricing.currency },
          { amount: originalPricing.totalPrice, currency: originalPricing.currency },
        )
      : null;
    currency = selectedRate.localPricing.currency;
    priceHistory = rateHistory as Array<{ fetchedAt?: Date | null }> | undefined;
  } else {
    priceDelta = model.priceDelta;
    currency = model.cost?.currency;
    priceHistory = model.priceHistory as Array<{ fetchedAt?: Date | null }> | undefined;
  }

  const originalEntry = priceHistory?.at(-1);
  const originalFetchedAt = originalEntry ? toDate((originalEntry as Record<string, unknown>).fetchedAt) : null;
  const originalDateStr = originalFetchedAt ? formatDate(originalFetchedAt, 'MMM d, yyyy') : null;
  const unitSuffix = priceDivisor > 1 && priceUnit ? ` ${priceUnit}` : '';

  // Transient feedback while recheck is in progress
  if (isRefreshing) {
    return (
      <span className={cn(indicatorBase, 'text-accent', className)}>
        <MaterialIcon icon="refresh" size={IconSizes.xs} className="animate-spin" />
      </span>
    );
  }

  // Transient feedback after recheck failed
  if (lastResult?.error) {
    return (
      <span className={cn(indicatorBase, 'text-destructive cursor-default', className)}>
        <MaterialIcon icon="error" size={IconSizes.xs} />
        Price check failed
      </span>
    );
  }

  // Stale -- show refresh button (only if refresh is allowed)
  if (isStale && canRefresh) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(indicatorBase, 'text-accent hover:text-accent-hover cursor-pointer', className)}
              onClick={refresh}
            >
              <MaterialIcon icon="refresh" size={IconSizes.xs} />
              Refresh
            </button>
          </TooltipTrigger>
          <TooltipContent>Price last checked {timeAgo}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Price increased
  if (priceDelta && priceDelta.direction === 'up' && currency) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(indicatorBase, 'text-red-600 cursor-default', className)}>
              <MaterialIcon icon="arrow_upward" size={IconSizes.xs} />
              {formatPrice(priceDelta.amount / priceDivisor, currency)}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Price increased {formatPrice(priceDelta.amount / priceDivisor, currency)}
            {unitSuffix} since {originalDateStr ?? 'original search'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Price decreased
  if (priceDelta && priceDelta.direction === 'down' && currency) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(indicatorBase, 'text-success cursor-default', className)}>
              <MaterialIcon icon="arrow_downward" size={IconSizes.xs} />
              {formatPrice(priceDelta.amount / priceDivisor, currency)}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Price decreased {formatPrice(priceDelta.amount / priceDivisor, currency)}
            {unitSuffix} since {originalDateStr ?? 'original search'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Fresh, no price change -- checkmark
  if (lastResult && lastResult.available && !lastResult.priceChanged) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(indicatorBase, 'text-success cursor-default', className)}>
              <MaterialIcon icon="check_circle" size={IconSizes.xs} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Price checked {timeAgo}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}
